// @flow

import * as React from 'react';
import { getRequest, createOperationDescriptor } from 'relay-runtime';
import {
  graphql,
  fetchQuery,
  useRelayEnvironment,
  useFragment,
  useMutation,
} from 'react-relay';
import { Flex, Box } from 'react-system';
import { css } from '@emotion/react';
import { parseISO, formatDistanceStrict } from 'date-fns';
import { Card, Divider, Checkbox } from '@material-ui/core';
import NoSsr from '@material-ui/core/NoSsr';
import { Carousel } from '@realadvisor/carousel';
import { Image } from '@realadvisor/image';
import { sanitizeHtml } from '@realadvisor/html-sanitizer';
import { useIsMobile } from '../../hooks/mobile';
import * as icons from '../../icons';
import { useTheme } from '../../theme';
import { useLocale } from '../../hooks/locale';
import { useUserData } from '../../hooks/user-data';
import { Link, useAsUrl } from '../../controls/link';
import { useAnalytics } from '../../controls/analytics';
import { useRouter } from '../../controls/router';
import { StructuredData } from '../../controls/structured-data';
import { getTextOfMarkdown } from '../../controls/marked';
import { translatePropertyType } from '../translate/propertyTypes';
import { AggregatesListingSaveFavorites } from './AggregatesListingSaveFavorites.js';
import { useListingStructuredData } from './useListingStructuredData';
import { makeListingHref } from './listing-href';
import type { AggregatesListingCardClickoutInsertMutation } from './__generated__/AggregatesListingCardClickoutInsertMutation.graphql';
import type { AggregatesListingCardFavouriteInsertOrDeleteMutation } from './__generated__/AggregatesListingCardFavouriteInsertOrDeleteMutation.graphql';
import type { AggregatesListingCard_listing$key } from './__generated__/AggregatesListingCard_listing.graphql';
import type { AggregatesListingCardMeQuery } from './__generated__/AggregatesListingCardMeQuery.graphql';

const textLoadingClassname = 'textLoadingClassname';
const imageLoadingInvisibleClassname = 'imageLoadingInvisibleClassname';
const svgLoadingInvisibleClassname = 'svgLoadingInvisibleClassname';
const textOverImageLoadingClassName = 'textOverImageLoadingClassName';

export const useGridLoadingStyle = (): any => {
  const { colors } = useTheme();
  return css`
    .${textLoadingClassname} {
      color: rgba(0, 0, 0, 0);
      outline: 2px solid white;
      background-color: ${colors.grey100};
    }

    .${textOverImageLoadingClassName} {
      color: rgba(0, 0, 0, 0);
      background-color: rgba(0, 0, 0, 0.03);
    }

    .${imageLoadingInvisibleClassname} {
      box-shadow: inset 1000px 0 0 0 ${colors.grey100};
      * {
        opacity: 0;
      }
    }

    .${svgLoadingInvisibleClassname} {
      opacity: 0;
      background-color: rgba(0, 0, 0, 1);
    }
  `;
};

const RatioContainer = ({ ratio, children }) => {
  const { shape, colors } = useTheme();
  return (
    <Box
      pt={ratio.map(r => Math.round(r * 100) + '%')}
      css={{
        position: 'relative',
        backgroundColor: colors.grey200,
        borderRadius: shape.borderRadius,
      }}
    >
      <Box css={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
        {children}
      </Box>
    </Box>
  );
};

type PropsView = {|
  listing: AggregatesListingCard_listing$key,
  offerType: string,
  titleComponent?: string,
  shadow?: boolean,
  border?: boolean,
  onLinkClick?: (SyntheticMouseEvent<HTMLLinkElement>) => void,
  withStructuredData?: boolean,
  isSimilar?: boolean,
  shallowLink: boolean,
  replaceLink: boolean,
  target?: string,

  onListingCardShowPopup?: (
    id: string,
    clusterIds: null | $ReadOnlyArray<number>,
    isSimilar: boolean,
  ) => void,
  onListingCardHidePopup?: () => void,
|};

const AggregatesListingCardStructuredData = props => {
  const router = useRouter();
  const { t } = useLocale();

  const listingStructuredData = useListingStructuredData(
    t,
    router.getUrl(props.href).absolute,
    props.listing,
  );

  const description =
    props.listing.shortTranslatedDescription ??
    props.listing.shortDescription ??
    '';

  return (
    <StructuredData
      type="SingleFamilyResidence"
      properties={{
        name: listingStructuredData.properties.name,
        description:
          props.listing.bulletPoints && props.listing.bulletPoints.length > 0
            ? props.listing.bulletPoints.join('. ').concat('.')
            : getTextOfMarkdown(description),
        image: listingStructuredData.properties.image,
        url: listingStructuredData.properties.url,
        floorSize: listingStructuredData.properties.floorSize ?? null,
        numberOfRooms: listingStructuredData.properties.numberOfRooms ?? null,
        address: listingStructuredData.properties.address,
        geo: listingStructuredData.properties.geo,
      }}
    />
  );
};

const IMAGE_RATIOS = [1 / 2, 8 / 11];
const CAROUSEL_MEDIA = IMAGE_RATIOS.map(ratio => ({
  itemsOnScreen: 1,
  aspectRatio: ratio,
  gap: 2,
}));

const IMAGE_SRCSET: any = IMAGE_RATIOS.map(ratio => [375, 375 * ratio]);

export const AggregatesListingCard: React.AbstractComponent<PropsView> =
  React.memo(props => {
    const environment = useRelayEnvironment();
    const isMobile = useIsMobile();

    const listing = useFragment(
      graphql`
        fragment AggregatesListingCard_listing on AggregatesListing
        @argumentDefinitions(
          filters: { type: "AggregatesListingFiltersInput", defaultValue: null }
        ) {
          ...useListingStructuredData_listing
          id
          slug
          encodedId
          offerType
          shortDescription: description(sentences: 3)
          shortTranslatedDescription: translatedDescription(sentences: 3)
          bulletPoints
          propertyType
          numberOfRooms
          livingSurface
          isFavourite
          usableSurface
          landSurface
          salePrice
          grossRentMonthly
          grossRentM2yearly
          currency
          postcode
          subLocality
          route
          streetNumber
          # read just 15 image, as image names are random and gziped hardly
          images(first: 100) {
            bucketName
            fileName
            blurhash
          }
          url(filters: $filters) {
            url
            originalDomain
            originalUrl
            mergedListingId
          }
          translatedTitle
          salePricePerLivingSurface
          salePricePerLandSurface
          createdAt
          propertyMainType
          compositePropertyType
          visitContactPerson
          agencyName
          agencyReference
          lotReference
          constructionYear
          locality
          stateObject {
            label
          }
          municipalityObject {
            label
          }
          lat
          lng
          address
          numberOfBathrooms
          showDetailPage
        }
      `,
      props.listing,
    );

    const { text, colors, shape } = useTheme();
    const [dialogOpen, setDialogOpen] = React.useState(false);

    const router = useRouter();

    const {
      dateLocale,
      t,
      numberLocale,
      currency: fallbackCurrency,
    } = useLocale();
    const { countryCode } = useUserData();
    const { track } = useAnalytics();

    const currentDate = React.useMemo(() => Date.now(), []);

    const meQuery = graphql`
      query AggregatesListingCardMeQuery {
        me {
          __typename
        }
      }
    `;

    const [insertOrDeleteListingCardFavourite] =
      useMutation<AggregatesListingCardFavouriteInsertOrDeleteMutation>(
        graphql`
          mutation AggregatesListingCardFavouriteInsertOrDeleteMutation(
            $input: InsertOrDeleteListingFavouriteInput!
          ) {
            insertOrDeleteListingFavourite(input: $input) {
              aggregatesListing {
                id
                isFavourite
              }
            }
          }
        `,
      );

    const [insertClickout] =
      useMutation<AggregatesListingCardClickoutInsertMutation>(
        graphql`
          mutation AggregatesListingCardClickoutInsertMutation(
            $input: InsertClickoutInput!
          ) {
            insertClickout(input: $input) {
              clientMutationId
            }
          }
        `,
      );

    const TitleComponent = props.titleComponent ?? 'div';

    const {
      id,
      slug,
      encodedId,
      isFavourite,
      numberOfRooms,
      livingSurface,
      usableSurface,
      landSurface,
      salePrice,
      currency,
      postcode,
      subLocality,
      route,
      streetNumber,
      grossRentMonthly,
      grossRentM2yearly,
      createdAt,
      salePricePerLivingSurface,
      salePricePerLandSurface,
      images,
      propertyMainType,
      propertyType,
      agencyName,
      visitContactPerson,
      url,
      shortDescription,
      shortTranslatedDescription,
      bulletPoints,
      showDetailPage,
    } = listing;

    const salePricePerComputedSurface =
      salePricePerLivingSurface != null
        ? salePricePerLivingSurface
        : salePricePerLandSurface;

    const description = shortTranslatedDescription ?? shortDescription ?? '';

    const formatCurrency = val =>
      numberLocale.format(val, {
        style: 'currency',
        currency: currency ?? fallbackCurrency,
        currencyDisplay: 'symbol',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });

    const formatNumber = val =>
      numberLocale.format(val, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });

    const realPrice = props.offerType === 'buy' ? salePrice : grossRentMonthly;

    const translatedPropertyType = translatePropertyType(
      t,
      propertyType ?? propertyMainType ?? '',
    ).propertyTypeSingularCapital;

    const features = [
      numberOfRooms != null && t('countRoomsShort', { count: numberOfRooms }),
      livingSurface != null
        ? `${formatNumber(livingSurface)} m²`
        : usableSurface != null
        ? `${formatNumber(usableSurface)} m²`
        : null,
      landSurface != null && `${formatNumber(landSurface)} m² ${t('plot')}`,
      translatedPropertyType,
    ]
      .filter(Boolean)
      .join(' • ');

    const propertyCardTitle =
      props.offerType === 'buy'
        ? t('propertyTypeCardTitle_sell', {
            propertyType: translatedPropertyType,
          })
        : t('propertyTypeCardTitle_rent', {
            propertyType: translatedPropertyType,
          });

    const formattedAddress = [
      [
        route != null ? [route, streetNumber].filter(Boolean).join(' ') : null,
        postcode,
      ]
        .filter(x => x != null)
        .join(', '),
      subLocality ?? '-',
    ].join(' ');

    const href = makeListingHref({
      offerType: listing.offerType,
      compositePropertyType: listing.compositePropertyType,
      slug: listing.slug,
    });

    const asHref = useAsUrl({ href });

    const renderFavoritesCheckbox = (checked: boolean) => (
      <Checkbox
        checked={checked}
        icon={
          <icons.Favourite
            className={svgLoadingInvisibleClassname}
            size={30}
            fill="rgba(0, 0, 0, 0.5)"
          />
        }
        checkedIcon={
          <icons.Favourite
            className={svgLoadingInvisibleClassname}
            size={30}
            fill={colors.red600}
          />
        }
        onChange={() => {
          const variables = {};
          fetchQuery<AggregatesListingCardMeQuery>(
            environment,
            meQuery,
            variables,
            {
              // prevent fetching if already loaded
              fetchPolicy: 'store-or-network',
            },
          ).subscribe({
            next: data => {
              // prevent gc me from store
              const request = getRequest(meQuery);
              const operation = createOperationDescriptor(request, variables);
              environment.retain(operation);
              if (data.me != null) {
                insertOrDeleteListingCardFavourite({
                  variables: {
                    input: {
                      listingId: id,
                      countryCode,
                    },
                  },
                  onCompleted: () => {
                    window.dataLayer.push({
                      event: 'add_to_wishlist',
                      listing_id: id,
                      listing_slug: slug,
                      listing_title: listing.translatedTitle,
                      listing_price: realPrice,
                      listing_currency: currency ?? fallbackCurrency,
                      listing_postcode: listing.postcode ?? '',
                      listing_offer_type: listing.offerType ?? '',
                      listing_property_main_type:
                        listing.propertyMainType ?? '',
                      listing_route: listing.route ?? '',
                      listing_street_number: listing.streetNumber ?? '',
                      listing_sub_locality: listing.subLocality ?? '',
                      listing_property_type: listing.propertyType ?? '',
                      listing_number_of_rooms: listing.numberOfRooms ?? '',
                      listing_living_surface: listing.livingSurface ?? '',
                      listing_land_surface: listing.landSurface ?? '',
                    });
                  },
                });
              } else {
                setDialogOpen(true);
              }
            },
          });
        }}
      />
    );

    const listingOnClick = e => {
      if (showDetailPage === true) {
        props.onLinkClick?.(e);
        return;
      }

      track('RA.Web.Listing.Linkout.Clicked', {
        category: 'Clickout',
        label: url?.originalDomain ?? '',
        id: url?.mergedListingId,
        encodedId: listing.encodedId,
        slug: listing.slug,
        offerType: listing.offerType,
        propertyType: listing.propertyType,
        propertyMainType: listing.propertyMainType,
        compositePropertyType: listing.compositePropertyType,
        numberOfRooms: listing.numberOfRooms,
        numberOfBathrooms: listing.numberOfBathrooms,
        livingSurface: listing.livingSurface,
        landSurface: listing.landSurface,
        salePrice: listing.salePrice,
        grossRentMonthly: listing.grossRentMonthly,
        currency: listing.currency,
        constructionYear: listing.constructionYear,
        locality: listing.locality,
        state: listing.stateObject?.label,
        municipality: listing.municipalityObject?.label,
        streetNumber: listing.streetNumber,
        route: listing.route,
        address: listing.address,
        postcode: listing.postcode,
        lat: listing.lat,
        lng: listing.lng,
        agencyName: listing.agencyName,
        agencyReference: listing.agencyReference,
        lotReference: listing.lotReference,
        url: url?.url,
      });

      window.dataLayer.push({
        event: 'click_out_aggregator',
        aggregator: url?.originalDomain,
        id: url?.mergedListingId,
        slug: listing.slug,
        offer_type: listing.offerType,
        property_type: listing.propertyType,
        property_main_type: listing.propertyMainType,
        composite_property_type: listing.compositePropertyType,
        number_of_rooms: listing.numberOfRooms,
        number_of_bathrooms: listing.numberOfBathrooms,
        living_surface: listing.livingSurface,
        land_surface: listing.landSurface,
        sale_price: listing.salePrice,
        gross_rent_monthly: listing.grossRentMonthly,
        currency: listing.currency,
        construction_year: listing.constructionYear,
        locality: listing.locality,
        state: listing.stateObject?.label,
        municipality: listing.municipalityObject?.label,
        street_number: listing.streetNumber,
        route: listing.route,
        address: listing.address,
        postcode: listing.postcode,
        lat: listing.lat,
        lng: listing.lng,
        agency_name: listing.agencyName,
        agency_reference: listing.agencyReference,
        lot_reference: listing.lotReference,
        url: listing.url?.url,
      });

      if (url) {
        const variables = {};
        fetchQuery<AggregatesListingCardMeQuery>(
          environment,
          meQuery,
          variables,
          {
            // prevent fetching if already loaded
            fetchPolicy: 'store-or-network',
          },
        ).subscribe({
          next: data => {
            // prevent gc me from store
            const request = getRequest(meQuery);
            const operation = createOperationDescriptor(request, variables);
            environment.retain(operation);
            if (data.me != null) {
              insertClickout({
                variables: {
                  input: {
                    listingId: url.mergedListingId,
                  },
                },
              });
            }
          },
        });
      }

      if (isMobile === true) {
        window.open(listing.url?.originalUrl, '_blank');
      } else {
        window.open(clickoutUrl, '_blank', 'width=1000,height=800');
      }

      e.preventDefault();
    };

    const clickoutUrl = router.getUrl({
      pathname: 'listing-clickout',
      query: { id: url?.url ?? '', listingSlug: listing.slug },
    }).absolute;

    return (
      <Box id={slug}>
        {
          /* Here we can just check "typeof window" because structured data render in header only */
          props.withStructuredData === true &&
            typeof window === 'undefined' && (
              <AggregatesListingCardStructuredData
                listing={listing}
                href={href}
              />
            )
        }
        <Card elevation={props.shadow === false ? 0 : 4}>
          <Flex width={1}>
            <Box
              flexGrow={1}
              css={{
                borderRadius: shape.borderRadius,
                position: 'relative',
              }}
            >
              {/*
                Here we do the trick to show dialog,
                change user browser url but leave all internal router query params as is,
                The trick that nextjs allows to pass into Link "as" property anything unrelated to real internal route.
               */}
              <Link
                href={
                  props.shallowLink
                    ? {
                        pathname: router.pathname,
                        query: router.query,
                      }
                    : href
                }
                as={showDetailPage === true ? asHref : '#'}
                shallow={props.shallowLink}
                // Replace if in dialog on client
                replace={props.replaceLink}
                scroll={false}
              >
                <a
                  css={{
                    display: 'block',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                  onClick={listingOnClick}
                  aria-label={
                    showDetailPage === true ? propertyCardTitle : undefined
                  }
                  className={imageLoadingInvisibleClassname}
                  rel="nofollow"
                  target={showDetailPage === true ? props.target : '_blank'}
                >
                  {images.length > 0 ? (
                    <Carousel
                      // do not reuse carousel for different items
                      key={id}
                      media={CAROUSEL_MEDIA}
                      // Render initially no more than 3 images
                      // This highly reduces amount of picture elements on rendering
                      renderUntilTouched={3}
                    >
                      {images.map((image, index) => (
                        <Image
                          key={index}
                          src={{
                            fileName: image.fileName,
                            bucketName: image.bucketName,
                          }}
                          srcSize={IMAGE_SRCSET}
                          alt={`${propertyCardTitle} — ${formattedAddress} - ${encodedId} - ${t(
                            'picture',
                          )} ${index + 1}`}
                          loading="lazy"
                          blurhash={image.blurhash}
                        />
                      ))}
                    </Carousel>
                  ) : (
                    <RatioContainer ratio={IMAGE_RATIOS}>
                      <Flex
                        flexDirection="column"
                        css={{
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                        }}
                      >
                        <Box>
                          <icons.Photo
                            className={svgLoadingInvisibleClassname}
                            fill={colors.grey400}
                            size={60}
                          />
                        </Box>
                        <Box
                          className={svgLoadingInvisibleClassname}
                          css={[
                            text.ellipsis,
                            text.subtitle1,
                            {
                              maxWidth: '100%',
                              color: colors.grey400,
                            },
                          ]}
                        >
                          {t('noPhotos')}
                        </Box>
                      </Flex>
                    </RatioContainer>
                  )}
                </a>
              </Link>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                css={{
                  position: 'absolute',
                  top: 0,
                  left: 8,
                  right: 0,
                  color: colors.white,
                  // zIndex is needed to fix scroll mobile issue
                  // when you can break scroll just touching carousel during scroll
                  zIndex: 1,
                }}
                mr={1}
              >
                <Flex
                  className={textOverImageLoadingClassName}
                  justifyContent="center"
                  css={[
                    {
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      borderRadius: '25px 25px 25px 25px',
                    },
                    text.strongCaption,
                  ]}
                  px={2}
                >
                  {formatDistanceStrict(parseISO(createdAt), currentDate, {
                    locale: dateLocale,
                    addSuffix: true,
                  })}
                </Flex>
                <NoSsr>{renderFavoritesCheckbox(isFavourite)}</NoSsr>
                <AggregatesListingSaveFavorites
                  open={dialogOpen}
                  listingId={id}
                  onClose={() => setDialogOpen(false)}
                />
              </Flex>
            </Box>
          </Flex>

          <Link
            href={
              props.shallowLink
                ? {
                    pathname: router.pathname,
                    query: router.query,
                  }
                : href
            }
            as={showDetailPage === true ? asHref : '#'}
            shallow={props.shallowLink}
            // Replace if in dialog on client
            replace={props.replaceLink}
            scroll={false}
          >
            <a
              onClick={listingOnClick}
              css={[
                {
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'block',
                },
                props.border === true
                  ? {
                      border: `solid 1px ${colors.grey300}`,
                      borderTop: 'none',
                      borderBottomRightRadius: shape.borderRadius,
                      borderBottomLeftRadius: shape.borderRadius,
                      overflow: 'hidden',
                    }
                  : {},
              ]}
              rel="nofollow"
              target={showDetailPage === true ? props.target : '_blank'}
            >
              <TitleComponent css={{ margin: 0 }}>
                <Box mx={3} my={2} flexGrow={1}>
                  <Box
                    className={textLoadingClassname}
                    css={[
                      text.strongCaption,
                      text.ellipsis,
                      {
                        color: colors.blueGrey400,
                        textAlign: 'left',
                      },
                    ]}
                  >
                    {(agencyName?.match(/Real\s*Advisor+/i)
                      ? visitContactPerson
                      : agencyName) ?? '—'}
                  </Box>
                  {propertyMainType != null && (
                    <Box
                      className={textLoadingClassname}
                      css={[text.ellipsis, text.strongCaption]}
                    >
                      {features.length > 0 ? features : '-'}
                    </Box>
                  )}{' '}
                  <Box
                    className={textLoadingClassname}
                    css={[text.strongCaption, text.ellipsis]}
                  >
                    {formattedAddress}
                  </Box>
                  <Box
                    css={[
                      text.strongCaption,
                      { color: colors.blueGrey400, minHeight: '60px' },
                    ]}
                  >
                    {bulletPoints && bulletPoints.length > 0 ? (
                      <Box className={textLoadingClassname}>
                        <ul
                          css={{
                            margin: 0,
                            padding: 0,
                            listStylePosition: 'inside',
                          }}
                        >
                          {bulletPoints.map((bulletPoint, index) => (
                            <li key={index} css={text.ellipsis}>
                              {bulletPoint}
                            </li>
                          ))}
                        </ul>
                      </Box>
                    ) : (
                      <Box
                        className={textLoadingClassname}
                        css={{
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 3,
                        }}
                      >
                        {sanitizeHtml(description, {
                          tagsBlacklist: [
                            'a',
                            'b',
                            'br',
                            'em',
                            'hr',
                            'i',
                            'li',
                            'ol',
                            'p',
                            's',
                            'strong',
                            'sub',
                            'sup',
                            'u',
                            'ul',
                          ],
                          attrsBlacklist: [],
                          attrsWithScheme: [],
                          allowedSchemes: [],
                          selfClosingTags: [],
                        })}
                      </Box>
                    )}
                  </Box>
                </Box>
              </TitleComponent>
              <Box flexGrow={1}>
                <Divider />
              </Box>
              <Box
                mb={2}
                mt={1}
                ml={3}
                mr={1}
                flexGrow={1}
                css={{
                  height: '49px',
                }}
                py={1}
              >
                <Box css={{ whiteSpace: 'nowrap' }}>
                  {realPrice != null && realPrice !== 0 ? (
                    <span className={textLoadingClassname} css={text.h6}>
                      {formatCurrency(realPrice)}
                      {countryCode !== 'FR' ? '.-' : ''}
                    </span>
                  ) : grossRentM2yearly != null && grossRentM2yearly >= 1.0 ? (
                    <Box className={textLoadingClassname} css={text.h6}>
                      {formatCurrency(grossRentM2yearly)} {t('totalRentM2Year')}
                    </Box>
                  ) : (
                    <span className={textLoadingClassname} css={text.h6}>
                      {t('onRequest')}
                    </span>
                  )}
                </Box>
                <Flex>
                  <Box flexGrow={1}>
                    {props.offerType === 'rent' ? (
                      realPrice != null &&
                      grossRentM2yearly != null &&
                      grossRentM2yearly >= 1.0 &&
                      // Not displaying price/sqm if it's more than 4 digit
                      grossRentM2yearly < 10000 ? (
                        <Box
                          className={textLoadingClassname}
                          css={[
                            text.strongCaption,
                            {
                              color: colors.blueGrey400,
                            },
                          ]}
                        >
                          {`${formatCurrency(grossRentM2yearly)} ${t(
                            'totalRentM2Year',
                          )}`}
                        </Box>
                      ) : (
                        <Box
                          className={textLoadingClassname}
                          css={[
                            text.strongCaption,
                            {
                              color: colors.blueGrey400,
                            },
                          ]}
                        >
                          {'—'}
                        </Box>
                      )
                    ) : salePricePerComputedSurface != null &&
                      props.offerType === 'buy' ? (
                      <Box
                        className={textLoadingClassname}
                        css={[
                          text.strongCaption,
                          {
                            color: colors.blueGrey400,
                          },
                        ]}
                      >
                        {`${formatCurrency(salePricePerComputedSurface)} / m²`}
                      </Box>
                    ) : (
                      <Box
                        className={textLoadingClassname}
                        css={[
                          text.strongCaption,
                          {
                            color: colors.blueGrey400,
                          },
                        ]}
                      >
                        {'—'}
                      </Box>
                    )}
                  </Box>
                  {url?.originalDomain.includes('realadvisor.ch') ? null : (
                    <Box
                      className={textLoadingClassname}
                      mr={1}
                      css={[
                        {
                          color: colors.blueGrey400,
                        },
                        text.strongCaption,
                      ]}
                    >
                      {url?.originalDomain.replace('www.', '')}
                    </Box>
                  )}
                </Flex>
              </Box>
            </a>
          </Link>
        </Card>
      </Box>
    );
  });
